import { AnyAction } from 'redux';
import { ErrorData } from '@synergy/api';
import { FormikErrors } from 'formik';

import {
    PRODUCT_DETAIL_PRICES_ERROR,
    PRODUCT_DETAIL_PRICES_RECEIVE,
    PRODUCT_DETAIL_PRICES_SUPPLIER_SPECIAL_OFFERS_RECEIVE,
    PRODUCT_DETAIL_PRICES_UPDATE,
} from './product-detail-prices-actions';
import { PRODUCT_DETAIL_RESET } from '../../store/product-detail-actions';
import { ProductPrice } from './../models/product-price';
import { ProductPriceFormValues } from '../models/form-values/product-price-form-values';
import { SupplierSpecialOffer } from '../models/supplier-special-offer';

export interface ProductDetailPricesReducerState {
    productDetailPrice: ProductPrice | null;
    /**
     * The prices as the server last handed them over (#2159). The copy above
     * cannot serve: leaving the tab parks its input right there.
     */
    baseline?: ProductPrice | null;
    requestError?: ErrorData;
    isDirty: boolean;
    isValid: boolean;
    errors: FormikErrors<ProductPrice>;
    supplierSpecialOffers: SupplierSpecialOffer[];
}

const initialProductDetailPricesReducerState: ProductDetailPricesReducerState = {
    productDetailPrice: null,
    isDirty: false,
    isValid: true,
    errors: {},
    supplierSpecialOffers: [],
};

export const productDetailPricesReducer = (
    state: ProductDetailPricesReducerState = initialProductDetailPricesReducerState,
    action: AnyAction
): ProductDetailPricesReducerState => {
    switch (action.type) {
        case PRODUCT_DETAIL_RESET: {
            return initialProductDetailPricesReducerState;
        }

        case PRODUCT_DETAIL_PRICES_ERROR: {
            return {
                ...state,
                requestError: action['errorData'],
            };
        }

        case PRODUCT_DETAIL_PRICES_RECEIVE: {
            return {
                ...state,
                productDetailPrice: action['prices'],
                // The same values, kept apart from the ones the tab edits.
                baseline: action['prices'],
                isDirty: false,
            };
        }

        case PRODUCT_DETAIL_PRICES_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const formDataCurrencyPrices = ((formData as ProductPriceFormValues).currencyPrices || []).map(
                currencyPrice => {
                    return {
                        ...currencyPrice,
                        scalePrices: currencyPrice?.scalePrices?.filter(
                            scalePrice => scalePrice && Object.keys(scalePrice).length > 0
                        ),
                        specialPrices: currencyPrice?.specialPrices?.filter(
                            specialPrice => specialPrice && Object.keys(specialPrice).length > 0
                        ),
                    };
                }
            );

            const nextDetailPrice: ProductPrice = {
                ...state.productDetailPrice,
                ...formData,
                currencyPrices: formDataCurrencyPrices,
                priceQuantity: formData.priceQuantity,
                priceReferenceQuantity: formData.priceReferenceQuantity,
            };

            return {
                ...state,
                productDetailPrice: nextDetailPrice,
                isDirty: state.isDirty || isDirty,
                isValid,
                errors,
            };
        }

        case PRODUCT_DETAIL_PRICES_SUPPLIER_SPECIAL_OFFERS_RECEIVE: {
            return {
                ...state,
                supplierSpecialOffers: action['offers'],
            };
        }

        default: {
            return state;
        }
    }
};
